import React from 'react';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { Box, Typography, CircularProgress, Checkbox, Tooltip, Avatar, Chip } from '@mui/material';

const DataTable = ({ 
    columns, 
    rows, 
    columnsWithActions,
    checkboxSelection,
    onRowSelectionModelChange,
    rowSelectionModel,
    disableRowSelectionOnClick,
    loading,
    height = 400,
    filterModel = { items: [] },
    ...props 
}) => {
    const getRowClassName = (params) => {
        return params.indexRelativeToCurrentPage % 2 === 0 ? 'even-row' : 'odd-row';
    };

    const customTheme = createTheme({
        components: {
            MuiDataGrid: {
                styleOverrides: {
                    root: {
                        height: '100% !important',
                        border: 'none',
                        '& .MuiDataGrid-main': {
                            // Remove outline on click
                            '&:focus-within': {
                                outline: 'none',
                            },
                        },
                        '& .MuiDataGrid-columnHeaders': {
                            backgroundColor: '#f8fafc',
                            borderBottom: '1px solid rgba(224, 224, 224, 0.4)',
                            '& .MuiDataGrid-columnSeparator': {
                                display: 'none',
                            },
                        },
                        '& .MuiDataGrid-columnHeader': {
                            color: 'rgba(0, 0, 0, 0.7)',
                            fontWeight: '600',
                            fontSize: '0.8125rem',
                            padding: '0 16px',
                            height: '56px',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                            },
                            '&:focus': {
                                outline: 'none',
                            },
                        },
                        '& .MuiDataGrid-columnHeaderCheckbox': {
                            width: '56px !important',
                            minWidth: '56px !important',
                            maxWidth: '56px !important',
                            '& .MuiDataGrid-columnHeaderTitleContainer': {
                                padding: '0',
                                justifyContent: 'center',
                            },
                        },
                        '& .MuiDataGrid-cellCheckbox': {
                            width: '56px !important',
                            minWidth: '56px !important',
                            maxWidth: '56px !important',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        },
                        '& .MuiDataGrid-columnHeaderTitle': {
                            fontWeight: 600,
                            letterSpacing: '0.01em',
                            textTransform: 'uppercase',
                            fontSize: '0.75rem',
                        },
                        '& .MuiDataGrid-columnHeaderTitleContainer': {
                            padding: '0 4px',
                        },
                        '& .MuiDataGrid-cell': {
                            borderBottom: '1px solid rgba(224, 224, 224, 0.3)',
                            fontSize: '0.875rem',
                            padding: '8px 16px',
                            height: '56px !important',
                            maxHeight: '56px !important',
                            '&:focus': {
                                outline: 'none',
                            },
                            '&:focus-within': {
                                outline: 'none',
                            },
                        },
                        '& .MuiDataGrid-row': {
                            transition: 'background-color 0.2s ease',
                            '&:hover': {
                                backgroundColor: 'rgba(25, 118, 210, 0.04)',
                            },
                            '&.Mui-selected': {
                                backgroundColor: 'rgba(25, 118, 210, 0.08)',
                                '&:hover': {
                                    backgroundColor: 'rgba(25, 118, 210, 0.12)',
                                },
                            },
                            '&:nth-of-type(odd)': {
                                backgroundColor: 'rgba(0, 0, 0, 0.005)',
                            },
                        },
                        '& .MuiDataGrid-footerContainer': {
                            borderTop: '1px solid rgba(224, 224, 224, 0.4)',
                            backgroundColor: '#f8fafc',
                            padding: '8px',
                            '& .MuiTablePagination-root': {
                                color: 'rgba(0, 0, 0, 0.7)',
                            }
                        },
                        '& .MuiCheckbox-root': {
                            color: 'rgba(0, 0, 0, 0.38)',
                            '&.Mui-checked': {
                                color: '#1976d2',
                            },
                            // Ensure checkbox is visible
                            padding: '5px',
                            margin: '0 auto',
                            display: 'flex',
                        },
                        '& .MuiDataGrid-virtualScroller::-webkit-scrollbar': {
                            width: '8px',
                            height: '8px',
                        },
                        '& .MuiDataGrid-virtualScroller::-webkit-scrollbar-track': {
                            background: '#f8fafc',
                            borderRadius: '8px',
                        },
                        '& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb': {
                            background: '#e0e0e0',
                            borderRadius: '8px',
                        },
                        '& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb:hover': {
                            background: '#bdbdbd',
                        },
                        // Status cell styling
                        '& .status-cell-active': {
                            color: '#388e3c',
                            backgroundColor: 'rgba(76, 175, 80, 0.1)',
                            borderRadius: '16px',
                            padding: '4px 12px',
                            fontWeight: 500,
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '0.75rem',
                        },
                        '& .status-cell-inactive': {
                            color: '#d32f2f',
                            backgroundColor: 'rgba(244, 67, 54, 0.1)',
                            borderRadius: '16px',
                            padding: '4px 12px',
                            fontWeight: 500,
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '0.75rem',
                        },
                        // Role cell styling
                        '& .role-cell': {
                            fontWeight: 500,
                        },
                        // Date cell styling  
                        '& .date-cell': {
                            color: 'rgba(0, 0, 0, 0.6)',
                            fontSize: '0.75rem',
                        }
                    },
                },
            },
            MuiButton: {
                styleOverrides: {
                    root: {
                        textTransform: 'none',
                        fontWeight: 500,
                    },
                },
            },
            MuiCheckbox: {
                styleOverrides: {
                    root: {
                        // Make sure the checkbox itself has a minimum size
                        padding: '5px',
                        '& .MuiSvgIcon-root': {
                            fontSize: '1.2rem',
                        },
                    },
                },
            },
            MuiToolbar: {
                styleOverrides: {
                    root: {
                        borderRadius: '8px 8px 0 0',
                    }
                }
            }
        },
    });

    // Enhance columns with custom cell rendering based on field types
    const enhancedColumns = (columnsWithActions || columns).map(column => {
        // For Status cells
        if (column.field === 'STATUS') {
            return {
                ...column,
                renderCell: (params) => {
                    const status = params.value?.toLowerCase() || '';
                    return (
                        <Chip
                            label={params.value || 'Unknown'}
                            size="small"
                            className={`status-cell-${status}`}
                            sx={{
                                backgroundColor: status === 'active' ? 'rgba(76, 175, 80, 0.1)' : 
                                                status === 'inactive' ? 'rgba(244, 67, 54, 0.1)' : 
                                                'rgba(158, 158, 158, 0.1)',
                                color: status === 'active' ? '#388e3c' : 
                                        status === 'inactive' ? '#d32f2f' : 
                                        '#616161',
                                fontWeight: 500,
                                borderRadius: '16px',
                                width: 'auto',
                                minWidth: '80px',
                                justifyContent: 'center'
                            }}
                        />
                    );
                }
            };
        }
        
        // For Role cells
        if (column.field === 'ROLE_NAME') {
            return {
                ...column,
                renderCell: (params) => (
                    <Typography 
                        variant="body2" 
                        className="role-cell"
                        sx={{ 
                            fontWeight: 500,
                            color: params.value?.toLowerCase().includes('admin') ? '#1976d2' : 'inherit'
                        }}
                    >
                        {params.value || '-'}
                    </Typography>
                )
            };
        }
        
        // For Date cells
        if (column.field === 'DATE_GRANTED' || column.field === 'DATE_REVOKED') {
            return {
                ...column,
                renderCell: (params) => {
                    const dateValue = params.value;
                    if (!dateValue || dateValue === '-') return <span>-</span>;
                    
                    let formattedDate;
                    try {
                        const date = new Date(dateValue);
                        formattedDate = new Intl.DateTimeFormat('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                        }).format(date);
                    } catch (error) {
                        formattedDate = dateValue;
                    }
                    
                    return (
                        <Typography variant="body2" className="date-cell">
                            {formattedDate}
                        </Typography>
                    );
                }
            };
        }
        
        // For Email cells
        if (column.field === 'EMAIL_ADDRESS') {
            return {
                ...column,
                renderCell: (params) => (
                    <Typography 
                        variant="body2"
                        sx={{ 
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            color: params.value === '-' ? 'text.secondary' : 'text.primary'
                        }}
                    >
                        {params.value || '-'}
                    </Typography>
                )
            };
        }
        
        // For all other cells
        return column;
    });

    return (
        <ThemeProvider theme={customTheme}>
            <Box 
                sx={{ 
                    height: height, 
                    width: '100%', 
                    position: 'relative',
                    overflow: 'hidden',
                    borderRadius: '12px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                }}
            >
                <DataGrid
                    rows={rows || []}
                    columns={enhancedColumns}
                    components={{
                        Toolbar: GridToolbar,
                        LoadingOverlay: () => (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    bottom: 0,
                                    right: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: 'rgba(255, 255, 255, 0.7)',
                                }}
                            >
                                <CircularProgress />
                            </Box>
                        ),
                        NoRowsOverlay: () => (
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    height: '100%',
                                    p: 3,
                                }}
                            >
                                <Typography variant="body1" color="text.secondary" align="center" sx={{ fontWeight: 500 }}>
                                    No data available
                                </Typography>
                                <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
                                    Try adjusting your filters or search criteria
                                </Typography>
                            </Box>
                        ),
                    }}
                    componentsProps={{
                        toolbar: {
                            showQuickFilter: true,
                            quickFilterProps: { debounceMs: 500 },
                            sx: {
                                p: 2,
                                backgroundColor: '#f8fafc',
                                borderBottom: '1px solid rgba(224, 224, 224, 0.4)',
                                borderRadius: '12px 12px 0 0',
                                '& .MuiButton-root': {
                                    fontWeight: 500,
                                    borderRadius: '8px',
                                    textTransform: 'none',
                                    padding: '6px 12px',
                                },
                                '& .MuiFormControl-root': {
                                    mx: 1,
                                },
                                '& .MuiInputBase-root': {
                                    borderRadius: '8px',
                                },
                                '& .MuiInputBase-input': {
                                    fontSize: '0.875rem',
                                },
                                '& .MuiIconButton-root': {
                                    padding: '8px',
                                }
                            },
                        },
                    }}
                    initialState={{
                        pagination: {
                            paginationModel: {
                                pageSize: 25,
                            },
                        },
                        filter: {
                            filterModel: filterModel,
                        },
                    }}
                    pageSizeOptions={[10, 25, 50, 100]}
                    checkboxSelection={checkboxSelection}
                    onRowSelectionModelChange={onRowSelectionModelChange}
                    rowSelectionModel={rowSelectionModel}
                    disableRowSelectionOnClick={disableRowSelectionOnClick}
                    loading={loading}
                    getRowClassName={getRowClassName}
                    getRowHeight={() => 'auto'}
                    disableColumnMenu={false}
                    disableDensitySelector={false}
                    disableColumnSelector={false}
                    disableColumnFilter={false}
                    slots={{
                        ...props.slots,
                        baseCheckbox: props.slots?.baseCheckbox || ((checkboxProps) => (
                            <Tooltip title={checkboxProps.checked ? "Deselect" : "Select"}>
                                <Checkbox {...checkboxProps} />
                            </Tooltip>
                        )),
                    }}
                    sx={{
                        '& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus': {
                            outline: 'none',
                        },
                        '& .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-columnHeader:focus': {
                            outline: 'none',
                        }
                    }}
                    {...props}
                />
            </Box>
        </ThemeProvider>
    );
};

export default DataTable;
